/** Rate Plan Assignment Dialog component. */
import React, { useState, useEffect, useCallback } from "react";
import { getCsrfToken } from "../../api/client";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  Assignment as AssignIcon,
  Upload as UploadIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";

/** API base URL. */
const API_BASE_URL = process.env.REACT_APP_API_URL || "";

/** Customer interface. */
interface Customer {
  id: string;
  name: string;
  account_type: string;
  current_rate_plan?: string;
}

/** Rate Plan interface. */
interface RatePlan {
  id: string;
  name: string;
}

/** Parsed CSV assignment. */
interface ParsedAssignment {
  customer_id: string;
  rate_plan_id: string;
  customer_name?: string;
  rate_plan_name?: string;
}

/** Props for RatePlanAssignDialog. */
interface RatePlanAssignDialogProps {
  /** Whether dialog is open */
  open: boolean;
  /** Close dialog callback */
  onClose: () => void;
  /** Rate plan ID (for single assignment mode) */
  ratePlanId?: string;
  /** Rate plan name (for display) */
  ratePlanName?: string;
  /** Callback after successful assignment */
  onAssignmentSuccess?: () => void;
}

/** TabPanel component. */
function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

/** Rate Plan Assignment Dialog component. */
export default function RatePlanAssignDialog({
  open,
  onClose,
  ratePlanId,
  ratePlanName,
  onAssignmentSuccess,
}: RatePlanAssignDialogProps) {
  const [tabValue, setTabValue] = useState(0);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [ratePlans, setRatePlans] = useState<RatePlan[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedRatePlan, setSelectedRatePlan] = useState<RatePlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [parsedAssignments, setParsedAssignments] = useState<ParsedAssignment[]>([]);
  const [csvLoading, setCsvLoading] = useState(false);

  /** Fetch customers without rate plans. */
  const fetchCustomers = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/customers?no_rate_plan=true`
      );
      if (!response.ok) throw new Error("Failed to load customers");
      const data = await response.json();
      setCustomers(data.customers || []);
    } catch (err: any) {
      console.error("Failed to load customers:", err);
    }
  }, []);

  /** Fetch active rate plans. */
  const fetchRatePlans = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/rate-plans?is_active=true`
      );
      if (!response.ok) throw new Error("Failed to load rate plans");
      const data = await response.json();
      setRatePlans(data.rate_plans || []);
    } catch (err: any) {
      console.error("Failed to load rate plans:", err);
    }
  }, []);

  /** Load data on mount. */
  useEffect(() => {
    if (open) {
      fetchCustomers();
      fetchRatePlans();
      // Pre-select rate plan if provided
      if (ratePlanId && ratePlanName) {
        setSelectedRatePlan({ id: ratePlanId, name: ratePlanName });
      }
    }
  }, [open, fetchCustomers, fetchRatePlans, ratePlanId, ratePlanName]);

  /** Handle single assignment. */
  const handleAssign = async () => {
    if (!selectedCustomer || !selectedRatePlan) {
      setError("Please select both a customer and a rate plan");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/rate-plans/assign`, {
        method: "POST",
        headers: {
          // CSRF: required by backend middleware on state changes
          "X-CSRF-Token": await getCsrfToken(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          customer_id: selectedCustomer.id,
          rate_plan_id: selectedRatePlan.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Assignment failed");
      }

      setSuccess(true);
      if (onAssignmentSuccess) {
        onAssignmentSuccess();
      }

      // Close dialog after showing success message
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || "Assignment failed");
    } finally {
      setLoading(false);
    }
  };

  /** Parse CSV file for bulk assignment. */
  const handleCsvFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCsvLoading(true);
    setError("");
    setParsedAssignments([]);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const text = evt.target?.result as string;
        const lines = text.split("\n").filter((l) => l.trim());

        if (lines.length === 0) {
          throw new Error("CSV file is empty");
        }

        // Check header
        const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
        const hasCustomerId = headers.some((h) =>
          h === "customer_id" || h === "customer"
        );
        const hasRatePlanId = headers.some((h) =>
          h === "rate_plan_id" || h === "rate_plan"
        );

        if (!hasCustomerId || !hasRatePlanId) {
          throw new Error(
            "CSV must have columns: customer_id, rate_plan_id"
          );
        }

        const assignments: ParsedAssignment[] = [];
        const customerIdIndex = headers.findIndex(
          (h) => h === "customer_id" || h === "customer"
        );
        const ratePlanIdIndex = headers.findIndex(
          (h) => h === "rate_plan_id" || h === "rate_plan"
        );

        for (let i = 1; i < lines.length; i++) {
          const cols = lines[i].split(",").map((c) => c.trim());
          if (cols.length >= 2 && cols[customerIdIndex] && cols[ratePlanIdIndex]) {
            assignments.push({
              customer_id: cols[customerIdIndex],
              rate_plan_id: cols[ratePlanIdIndex],
            });
          }
        }

        if (assignments.length === 0) {
          throw new Error("No valid assignments found in CSV");
        }

        // Look up names for display
        const enrichedAssignments = assignments.map((assignment) => {
          const customer = customers.find((c) => c.id === assignment.customer_id);
          const ratePlan = ratePlans.find((rp) => rp.id === assignment.rate_plan_id);
          return {
            ...assignment,
            customer_name: customer?.name,
            rate_plan_name: ratePlan?.name,
          };
        });

        setParsedAssignments(enrichedAssignments);
        setCsvFile(file);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setCsvLoading(false);
      }
    };

    reader.readAsText(file);
  };

  /** Handle bulk assignment. */
  const handleBulkAssign = async () => {
    if (parsedAssignments.length === 0) {
      setError("No assignments to process");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/rate-plans/assign-bulk`, {
        method: "POST",
        headers: {
          // CSRF: required by backend middleware on state changes
          "X-CSRF-Token": await getCsrfToken(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          assignments: parsedAssignments,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Bulk assignment failed");
      }

      setSuccess(true);
      if (onAssignmentSuccess) {
        onAssignmentSuccess();
      }

      // Close dialog after showing success message
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || "Bulk assignment failed");
    } finally {
      setLoading(false);
    }
  };

  /** Handle close. */
  const handleClose = () => {
    setSelectedCustomer(null);
    setSelectedRatePlan(null);
    setParsedAssignments([]);
    setCsvFile(null);
    setError("");
    setSuccess(false);
    setTabValue(0);
    onClose();
  };

  /** Check if customer already has a rate plan. */
  const hasExistingPlan = selectedCustomer && selectedCustomer.current_rate_plan;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AssignIcon />
          <Typography variant="h6" component="span">
            Assign Rate Plan to Customer
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tab label="Single Assignment" />
          <Tab label="Bulk Assignment (CSV)" />
        </Tabs>

        {/* Single Assignment Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            {/* Customer Selection */}
            <Autocomplete
              options={customers}
              getOptionLabel={(option) => option.name}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.account_type}
                      {option.current_rate_plan && ` - Current: ${option.current_rate_plan}`}
                    </Typography>
                  </Box>
                </Box>
              )}
              value={selectedCustomer}
              onChange={(_, newValue) => setSelectedCustomer(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Customer"
                  placeholder="Search customers..."
                  fullWidth
                />
              )}
              noOptionsText="No customers available"
            />

            {/* Rate Plan Selection */}
            {!ratePlanId && (
              <Autocomplete
                options={ratePlans}
                getOptionLabel={(option) => option.name}
                value={selectedRatePlan}
                onChange={(_, newValue) => setSelectedRatePlan(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Rate Plan"
                    placeholder="Search rate plans..."
                    fullWidth
                  />
                )}
                noOptionsText="No rate plans available"
              />
            )}

            {ratePlanId && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Rate Plan: <strong>{ratePlanName}</strong>
                </Typography>
              </Box>
            )}

            {/* Warning if customer has existing plan */}
            {hasExistingPlan && (
              <Alert severity="warning" icon={<WarningIcon />}>
                This customer already has rate plan "{selectedCustomer.current_rate_plan}". Assigning a new plan will replace it.
              </Alert>
            )}

            {/* Success Message */}
            {success && (
              <Alert severity="success">
                Rate plan assigned successfully!
              </Alert>
            )}

            {/* Error Message */}
            {error && (
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            )}
          </Box>
        </TabPanel>

        {/* Bulk Assignment Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Upload a CSV file with columns: customer_id, rate_plan_id
            </Typography>

            <Box
              sx={{
                border: "2px dashed",
                borderColor: "divider",
                borderRadius: 1,
                p: 3,
                textAlign: "center",
                cursor: "pointer",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
              onClick={() => document.getElementById("csv-upload-input")?.click()}
            >
              <input
                id="csv-upload-input"
                type="file"
                accept=".csv"
                hidden
                onChange={handleCsvFileChange}
              />
              {csvLoading ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <UploadIcon sx={{ fontSize: 48, color: "action.active" }} />
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    {csvFile ? csvFile.name : "Click to upload CSV file"}
                  </Typography>
                </>
              )}
            </Box>

            {/* Preview Table */}
            {parsedAssignments.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Preview ({parsedAssignments.length} assignments)
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Customer ID</TableCell>
                        <TableCell>Customer Name</TableCell>
                        <TableCell>Rate Plan ID</TableCell>
                        <TableCell>Rate Plan Name</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedAssignments.slice(0, 10).map((assignment, i) => (
                        <TableRow key={i}>
                          <TableCell>{assignment.customer_id}</TableCell>
                          <TableCell>{assignment.customer_name || "Unknown"}</TableCell>
                          <TableCell>{assignment.rate_plan_id}</TableCell>
                          <TableCell>{assignment.rate_plan_name || "Unknown"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {parsedAssignments.length > 10 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    ... and {parsedAssignments.length - 10} more assignments
                  </Typography>
                )}
              </Box>
            )}

            {/* Success Message */}
            {success && (
              <Alert severity="success">
                Bulk assignment completed successfully!
              </Alert>
            )}

            {/* Error Message */}
            {error && (
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            )}
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        {tabValue === 0 && (
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={loading || !selectedCustomer || !selectedRatePlan}
            startIcon={loading ? <CircularProgress size={20} /> : <AssignIcon />}
          >
            {loading ? "Assigning..." : "Assign"}
          </Button>
        )}
        {tabValue === 1 && parsedAssignments.length > 0 && (
          <Button
            variant="contained"
            onClick={handleBulkAssign}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <AssignIcon />}
          >
            {loading ? "Assigning..." : `Assign All (${parsedAssignments.length})`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
